import { addOnSelectionSnapshotSchema, pricingRequestSchema } from './schemas';
import { normalizeSelectedAddOns } from './addOnSelections';
import type {
  AddOn,
  AddOnSelectionSnapshot,
  Discount,
  PricingRequest,
  PricingResult,
  SelectedAddOn,
  Service,
} from './types';

const KG_SCALE = 1000;

function kgToThousandths(weightKg: number): number {
  const normalized = weightKg.toFixed(3);
  return Math.round(Number(normalized) * KG_SCALE);
}

function multiplyByKgCentavos(priceCentavos: number, weightKg: number): number {
  const kgThousandths = kgToThousandths(weightKg);
  return Math.floor((priceCentavos * kgThousandths + KG_SCALE / 2) / KG_SCALE);
}

function multiplyByQuantity(unitPriceCentavos: number, quantity: number): number {
  if (!Number.isInteger(quantity)) {
    throw new Error('Quantity must be a whole number for item and fixed-price quantities.');
  }
  return unitPriceCentavos * quantity;
}

function calculateBaseSubtotal(request: PricingRequest): number {
  const { service } = request;

  switch (service.pricingType) {
    case 'PER_LOAD': {
      const weightKg = request.weightKg ?? 0;
      const quantity = request.quantity ?? 1;
      if (weightKg <= 0) {
        throw new Error(`${service.name} requires the load weight in KG.`);
      }
      const capacityKg = service.loadCapacityKg;
      if (capacityKg && weightKg > capacityKg * quantity) {
        throw new Error(`${service.name} allows up to ${capacityKg} KG per load.`);
      }
      return multiplyByQuantity(service.priceCentavos, quantity);
    }
    case 'PER_KG': {
      const weightKg = request.weightKg ?? 0;
      if (service.requiresWeight && weightKg <= 0) {
        throw new Error(`${service.name} requires a weight greater than 0 KG.`);
      }
      if (service.minimumKg !== undefined && weightKg > 0 && weightKg < service.minimumKg) {
        throw new Error(`${service.name} requires a minimum of ${service.minimumKg} KG.`);
      }
      const quantity = request.quantity ?? 1;
      return multiplyByKgCentavos(service.priceCentavos, weightKg) * quantity;
    }
    case 'PER_ITEM': {
      const items = request.items ?? [];
      if (service.requiresItemSelection && items.length === 0) {
        throw new Error(`${service.name} requires at least one service item.`);
      }
      return items.reduce(
        (total, selection) =>
          total + multiplyByQuantity(selection.item.unitPriceCentavos, selection.quantity),
        0,
      );
    }
    case 'FIXED': {
      const quantity = request.quantity ?? 1;
      return multiplyByQuantity(service.priceCentavos, quantity);
    }
  }
}

function calculateAddOn(
  addOnSelection: SelectedAddOn,
  weightKg: number,
  itemQuantity: number,
): number {
  const { addOn } = addOnSelection;
  const quantity = addOnSelection.quantity ?? 1;

  switch (addOn.pricingType) {
    case 'FLAT':
      return addOn.priceCentavos * quantity;
    case 'PER_KG':
      return multiplyByKgCentavos(addOn.priceCentavos, weightKg) * quantity;
    case 'PER_ITEM':
      // A per-item add-on still applies to a non-item service when the cashier
      // explicitly selects it. For itemized services, it applies to every item.
      return multiplyByQuantity(addOn.priceCentavos, Math.max(itemQuantity, 1)) * quantity;
  }
}

function calculateAddOns(addOns: SelectedAddOn[], weightKg: number, itemQuantity: number): number {
  return addOns.reduce((total, addOn) => total + calculateAddOn(addOn, weightKg, itemQuantity), 0);
}

function createAddOnSnapshots(request: PricingRequest): AddOnSelectionSnapshot[] {
  if (request.service.allowsAddons === false) return [];
  const normalized = normalizeSelectedAddOns(request.addOns);
  const weightKg = request.weightKg ?? 0;
  const itemQuantity = (request.items ?? []).reduce((total, item) => total + item.quantity, 0);
  return normalized.map(({ addOn, quantity = 1 }) =>
    addOnSelectionSnapshotSchema.parse({
      addonId: addOn.id,
      nameSnapshot: addOn.name,
      pricingType: addOn.pricingType,
      unitPriceCentavos: addOn.priceCentavos,
      quantity,
      lineTotalCentavos: calculateAddOn({ addOn, quantity }, weightKg, itemQuantity),
    }),
  );
}

function calculateDiscount(discount: Discount | undefined, subtotalCentavos: number): number {
  if (!discount) return 0;

  if (discount.type === 'FIXED') {
    return Math.min(discount.amountCentavos, subtotalCentavos);
  }

  const percentageBasisPoints = Math.round(discount.percentage * 100);
  return Math.min(
    Math.floor((subtotalCentavos * percentageBasisPoints + 5000) / 10000),
    subtotalCentavos,
  );
}

function validatePricingRequest(request: PricingRequest): void {
  pricingRequestSchema.parse(request);

  const requestedQuantity = request.quantity ?? 1;
  if (request.service.allowsQuantity === false && requestedQuantity !== 1) {
    throw new Error(`${request.service.name} does not allow quantity changes.`);
  }

  if (request.service.pricingType === 'PER_ITEM') {
    for (const selection of request.items ?? []) {
      if (selection.item.serviceId !== request.service.id) {
        throw new Error(
          `Service item ${selection.item.id} does not belong to ${request.service.id}.`,
        );
      }
    }
  }
}

export class PricingEngine {
  static calculate(request: PricingRequest): PricingResult {
    validatePricingRequest(request);

    const baseSubtotalCentavos = calculateBaseSubtotal(request);
    const itemQuantity = (request.items ?? []).reduce((total, item) => total + item.quantity, 0);
    const weightKg = request.weightKg ?? 0;
    const addOnsSubtotalCentavos =
      request.service.allowsAddons === false
        ? 0
        : calculateAddOns(normalizeSelectedAddOns(request.addOns), weightKg, itemQuantity);
    const subtotalCentavos = baseSubtotalCentavos + addOnsSubtotalCentavos;
    const discountCentavos = calculateDiscount(request.discount, subtotalCentavos);

    return {
      baseSubtotalCentavos,
      addOnsSubtotalCentavos,
      subtotalCentavos,
      discountCentavos,
      grandTotalCentavos: subtotalCentavos - discountCentavos,
    };
  }

  static normalizeAddOns(addOns: SelectedAddOn[] = []): SelectedAddOn[] {
    return normalizeSelectedAddOns(addOns);
  }

  static createAddOnSnapshots(request: PricingRequest): AddOnSelectionSnapshot[] {
    pricingRequestSchema.parse(request);
    return createAddOnSnapshots(request);
  }

  static formatCentavos(centavos: number): string {
    return `₱${(centavos / 100).toFixed(2)}`;
  }

  static isServiceActive(service: Service): boolean {
    return service.active !== false;
  }

  static isAddOnActive(addOn: AddOn): boolean {
    return addOn.active !== false;
  }
}
